// client/src/coms/customMedia.js
// Resolves custom emoji / sticker ids seen in messages and reactions (batched, cached) and renders them.
// Images come only from Voxemera's own authenticated media route, never from user-supplied URLs.
import React, { useEffect, useState } from 'react';
import { comsApiGet } from './comsApi';

export const CUSTOM_EMOJI_TOKEN_RE = /<(a?):([a-z0-9_]{2,32}):(\d{1,12})>/g;
export const STICKER_MESSAGE_RE = /^<sticker:(\d{1,12})>$/;

function createResolver(path, listKey) {
  const cache = new Map(); // id -> item | null (missing)
  const waiting = new Map(); // id -> [resolve]
  let timer = null;

  async function flush() {
    timer = null;
    const ids = Array.from(waiting.keys()).slice(0, 100);
    if (!ids.length) return;
    const batch = new Map(ids.map((id) => [id, waiting.get(id)]));
    ids.forEach((id) => waiting.delete(id));
    let items = [];
    try {
      const r = await comsApiGet(`${path}?ids=${ids.join(',')}`);
      items = Array.isArray(r && r[listKey]) ? r[listKey] : [];
    } catch {
      items = [];
    }
    const byId = new Map(items.map((x) => [String(x.id), x]));
    batch.forEach((resolvers, id) => {
      const item = byId.get(id) || null;
      cache.set(id, item);
      resolvers.forEach((fn) => fn(item));
    });
    if (waiting.size) timer = setTimeout(flush, 0);
  }

  function resolve(id) {
    const key = String(id || '');
    if (!key) return Promise.resolve(null);
    if (cache.has(key)) return Promise.resolve(cache.get(key));
    return new Promise((res) => {
      const list = waiting.get(key) || [];
      list.push(res);
      waiting.set(key, list);
      if (!timer) timer = setTimeout(flush, 30);
    });
  }

  function peek(id) {
    return cache.get(String(id || ''));
  }

  function prime(items) {
    (items || []).forEach((x) => x && x.id != null && cache.set(String(x.id), x));
  }

  return { resolve, peek, prime };
}

export const emojiResolver = createResolver('/emojis/resolve', 'emojis');
export const stickerResolver = createResolver('/stickers/resolve', 'stickers');

function useResolved(resolver, id) {
  const [item, setItem] = useState(() => resolver.peek(id));
  useEffect(() => {
    let alive = true;
    resolver.resolve(id).then((x) => {
      if (alive) setItem(x);
    });
    return () => {
      alive = false;
    };
  }, [resolver, id]);
  return item;
}

export function CustomEmoji({ id, name, size = 22, title }) {
  const item = useResolved(emojiResolver, id);
  if (item === null) return <span title="Unavailable emoji">{`:${name}:`}</span>;
  if (!item) return <span style={{ display: 'inline-block', width: size, height: size, verticalAlign: 'middle' }} aria-label={`:${name}:`} />;
  return (
    <img
      src={item.url}
      alt={`:${item.name}:`}
      title={title || `:${item.name}:`}
      draggable={false}
      loading="lazy"
      style={{ width: size, height: size, objectFit: 'contain', verticalAlign: 'middle', display: 'inline-block' }}
    />
  );
}

export function StickerImage({ id, size = 160 }) {
  const item = useResolved(stickerResolver, id);
  const [broken, setBroken] = useState(false);
  if (broken) {
    // The record exists but its image file is missing on the server.
    return <div style={{ fontSize: 12, opacity: 0.6, fontStyle: 'italic' }}>{item && item.name ? `Sticker “${item.name}” (image unavailable)` : 'Sticker image unavailable'}</div>;
  }
  if (item === null) {
    return <div style={{ fontSize: 12, opacity: 0.6, fontStyle: 'italic' }}>Sticker unavailable</div>;
  }
  if (item && item.removed) {
    return <div style={{ fontSize: 12, opacity: 0.6, fontStyle: 'italic' }}>Sticker removed by moderation</div>;
  }
  return (
    <div style={{ width: size, height: size, marginTop: 2 }}>
      {item ? (
        <img src={item.url} alt={item.name} title={item.name} draggable={false} loading="lazy" onError={() => setBroken(true)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : null}
    </div>
  );
}

// Splits text into strings and custom-emoji elements. `renderText(str, key)` renders the plain parts.
export function renderWithCustomEmoji(text, renderText, keyPrefix = 'ce', { onlyEmoji = false } = {}) {
  const s = String(text == null ? '' : text);
  const out = [];
  let last = 0;
  let k = 0;
  const re = new RegExp(CUSTOM_EMOJI_TOKEN_RE.source, 'g');
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) {
      const part = renderText(s.slice(last, m.index), `${keyPrefix}-t${k++}`);
      if (Array.isArray(part)) out.push(...part);
      else if (part != null) out.push(part);
    }
    out.push(<CustomEmoji key={`${keyPrefix}-e${k++}`} id={m[3]} name={m[2]} size={onlyEmoji ? 44 : 22} />);
    last = m.index + m[0].length;
  }
  if (last < s.length) {
    const part = renderText(s.slice(last), `${keyPrefix}-t${k++}`);
    if (Array.isArray(part)) out.push(...part);
    else if (part != null) out.push(part);
  }
  return out;
}

export function isOnlyCustomEmoji(text) {
  const s = String(text || '').trim();
  if (!s) return false;
  return s.replace(new RegExp(CUSTOM_EMOJI_TOKEN_RE.source, 'g'), '').trim() === '' && s.length <= 400;
}

// A reaction is either a unicode emoji or a custom token.
export function ReactionEmoji({ emoji, size = 18 }) {
  const m = /^<(a?):([a-z0-9_]{2,32}):(\d{1,12})>$/.exec(String(emoji || ''));
  if (!m) return <span>{emoji}</span>;
  return <CustomEmoji id={m[3]} name={m[2]} size={size} />;
}
